import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { TicketFormData } from "@features/events/types";

export interface TicketValidationErrors {
  title?: string;
  price?: string;
  minQuantity?: string;
  startsAt?: string;
  expiresAt?: string;
}

export interface TouchedFields {
  price?: boolean;
  minQuantity?: boolean;
}

export function useTicketValidation() {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<TicketValidationErrors>({});

  const validate = useCallback(
    (
      formData: TicketFormData,
      selectedLanguages: string[],
      isEditing: boolean,
      touchedFields: TouchedFields
    ): boolean => {
      const newErrors: TicketValidationErrors = {};
      const today = dayjs().startOf("day");

      // Title validation - all selected languages must be filled
      if (selectedLanguages.length > 0) {
        const title = formData.title ?? {};
        const missingLanguages = selectedLanguages.filter(
          (lang) => !title[lang]?.trim()
        );
        if (missingLanguages.length > 0) {
          newErrors.title = t("eventDetails.tickets.validation.titleRequired");
        }
      }

      // Price validation (skip if free)
      if (!formData.isFree) {
        if (Number.isNaN(formData.price)) {
          newErrors.price = t("eventDetails.tickets.validation.priceRequired");
        } else if (!isEditing && !touchedFields.price) {
          // New ticket and field not touched - require user input
          newErrors.price = t("eventDetails.tickets.validation.priceRequired");
        } else if (formData.price < 0) {
          newErrors.price = t("eventDetails.tickets.validation.priceMin");
        }
      }

      // Min quantity validation
      if (Number.isNaN(formData.minQuantity)) {
        newErrors.minQuantity = t(
          "eventDetails.tickets.validation.minQuantityRequired"
        );
      } else if (!isEditing && !touchedFields.minQuantity) {
        // New ticket and field not touched - require user input
        newErrors.minQuantity = t(
          "eventDetails.tickets.validation.minQuantityRequired"
        );
      } else if (formData.minQuantity < 1) {
        newErrors.minQuantity = t(
          "eventDetails.tickets.validation.minQuantityMin"
        );
      }

      // Date validations (only when enabled and date is provided)
      if (formData.hasStart && formData.startsAt) {
        const startsAt = dayjs(formData.startsAt);
        if (startsAt.isBefore(today)) {
          newErrors.startsAt = t(
            "eventDetails.tickets.validation.startsAtPast"
          );
        }
      }

      if (formData.hasExpiry && formData.expiresAt) {
        const expiresAt = dayjs(formData.expiresAt);
        if (expiresAt.isBefore(today) || expiresAt.isSame(today)) {
          newErrors.expiresAt = t(
            "eventDetails.tickets.validation.expiresAtPast"
          );
        }
      }

      // Cross-validation: startsAt must be before expiresAt
      if (
        formData.hasStart &&
        formData.hasExpiry &&
        formData.startsAt &&
        formData.expiresAt
      ) {
        const startsAt = dayjs(formData.startsAt);
        const expiresAt = dayjs(formData.expiresAt);
        if (!startsAt.isBefore(expiresAt)) {
          newErrors.startsAt = t(
            "eventDetails.tickets.validation.startsBeforeExpires"
          );
        }
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [t]
  );

  const clearErrors = useCallback(() => setErrors({}), []);

  return { errors, validate, clearErrors };
}

